/**
 * @fileoverview A program's Settings: archive it, restore it, or delete it.
 *
 * Archiving is the reversible one and the one this card leads with: every
 * class's meetings from today on are cancelled, the listing closes and the
 * program moves to Past, and everything else is kept for a restore
 * (`POST /org/programs/:id/archive`). Deleting cannot be undone, so it sits
 * apart, behind a dialog that says what goes and what stays, offers archiving
 * instead to anyone who wants the program's details later, and asks for the
 * program's name before it will run (`POST /org/programs/:id/delete`). A
 * deleted program's students leave the organization's lists rather than
 * reading as inactive; lessons already taught stay with their instructors.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, Card, Icon, SectionHeader } from "@/components/studio/primitives";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : word.endsWith("s") ? "es" : "s"}`;

function archivedOn(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const body = "font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]";

/**
 * @param {{ program: object, onChanged?: (program: object) => void }} props
 */
export default function ProgramLifecycleSettings({ program, onChanged }) {
  const navigate = useNavigate();
  // Which write is running ("archive" | "restore" | "delete"), or null.
  const [busy, setBusy] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const archived = program.status === "closed";
  const title = program.title || "this program";
  const nameMatches = typed.trim().toLowerCase() === (program.title || "").trim().toLowerCase();

  const archive = async ({ ask = true } = {}) => {
    if (
      ask &&
      !window.confirm(
        `Archive "${title}"?\n\nEvery class's meetings from today on are cancelled and the instructors are told. It leaves the marketplace and moves to Past. Classes, rosters, notes, lessons and settings are all kept, and you can restore it later.`,
      )
    )
      return;
    setBusy("archive");
    try {
      const res = await orgApi.post(`/org/programs/${program.program_id}/archive`);
      const { classes_archived: classes = 0, meetings_cancelled: meetings = 0, classes_left: left = [] } = res.data || {};
      toast.success(
        classes
          ? `Archived: ${plural(classes, "class")} and ${plural(meetings, "upcoming meeting")} taken off the calendar`
          : "Archived",
      );
      if (left.length) {
        toast(`${left.join(", ")} ${left.length === 1 ? "is" : "are"} booked in Kalver Scheduler. Cancel ${left.length === 1 ? "it" : "them"} there.`);
      }
      setConfirming(false);
      onChanged?.(res.data?.program);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't archive the program"));
    } finally {
      setBusy(null);
    }
  };

  const restore = async () => {
    setBusy("restore");
    try {
      const res = await orgApi.post(`/org/programs/${program.program_id}/unarchive`);
      const n = res.data?.classes_restored || 0;
      toast.success(n ? `Restored, with ${plural(n, "class")} back on the calendar` : "Restored");
      onChanged?.(res.data?.program);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't restore the program"));
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!nameMatches) return;
    setBusy("delete");
    try {
      await orgApi.post(`/org/programs/${program.program_id}/delete`);
      toast.success(`Deleted ${title}`);
      setConfirming(false);
      navigate("/org/programs");
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't delete the program"));
    } finally {
      setBusy(null);
    }
  };

  const openDelete = () => {
    setTyped("");
    setConfirming(true);
  };

  return (
    <>
      <Card className="mb-6" data-testid="program-lifecycle">
        <SectionHeader>{archived ? "Archived" : "Archive or delete"}</SectionHeader>
        {archived ? (
          <>
            <p className={`mb-4 ${body}`} data-testid="program-archived-since">
              Archived{archivedOn(program.archived_at) ? ` on ${archivedOn(program.archived_at)}` : ""}. It no longer
              takes applications or lessons, and its classes&rsquo; meetings from that day on were cancelled. Everything
              else is kept. Restoring reopens it and puts its classes back on the calendar; meetings whose date has
              passed stay cancelled.
            </p>
            <Button variant="ghost" icon="archiveRestore" onPress={restore} disabled={!!busy} data-testid="restore-program">
              Restore program
            </Button>
          </>
        ) : (
          <>
            <p className={`mb-4 ${body}`}>
              Archiving stops the program: every class&rsquo;s meetings from today on are cancelled, it comes off the
              marketplace and the directory, and it moves to Past. Its classes, rosters, notes, lessons and settings are
              all kept, and you can restore it at any time.
            </p>
            <Button variant="ghost" icon="archive" onPress={() => archive()} disabled={!!busy} data-testid="archive-program">
              Archive program
            </Button>
          </>
        )}

        <div className="mt-6 border-t border-[var(--studio-divider)] pt-5">
          <p className={`mb-4 ${body}`}>
            Deleting removes the program for good: its classes, rosters, files and settings are deleted, and its
            students are removed from your organization&rsquo;s lists.
          </p>
          <Button variant="danger" icon="trash" onPress={openDelete} disabled={!!busy} data-testid="delete-program">
            Delete program
          </Button>
        </div>
      </Card>

      <Dialog open={confirming} onOpenChange={(open) => !open && !busy && setConfirming(false)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[520px]" data-testid="delete-program-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-semibold">Delete {title}?</DialogTitle>
            <DialogDescription className="font-sans text-[14px] text-[var(--text-secondary)]">
              This can&rsquo;t be undone.
            </DialogDescription>
          </DialogHeader>

          <ul className={`flex list-disc flex-col gap-1.5 pl-5 ${body}`}>
            <li>Its classes, rosters, lesson slots, files and settings are deleted.</li>
            <li>Its students are removed from your organization&rsquo;s lists, not marked inactive.</li>
            <li>Its instructors lose access to it, and it leaves the marketplace.</li>
            <li>Lessons already taught stay with the instructors who taught them and still count toward your usage.</li>
          </ul>

          {!archived && (
            <div
              className="flex flex-col gap-3 rounded-[14px] border border-[var(--accent-yellow-border-strong)] bg-[var(--accent-yellow-dim)] p-4"
              data-testid="delete-suggest-archive"
            >
              <p className="flex items-start gap-2 font-sans text-[14px] leading-relaxed text-[var(--text-primary)]">
                <Icon name="info" size={16} className="mt-0.5 shrink-0 text-[var(--accent-yellow-text)]" />
                <span>
                  Want to keep the program&rsquo;s details? Archive it instead. It stops running, everything is kept, and
                  you can restore it later.
                </span>
              </p>
              <div>
                <Button
                  variant="ghost"
                  icon="archive"
                  onPress={() => archive({ ask: false })}
                  disabled={!!busy}
                  data-testid="delete-archive-instead"
                >
                  Archive instead
                </Button>
              </div>
            </div>
          )}

          <label className="block">
            <span className="mb-1.5 block font-sans text-[13px] font-medium text-[var(--text-tertiary)]">
              Type <span className="font-semibold text-[var(--text-primary)]">{program.title}</span> to confirm
            </span>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              aria-label="Program name"
              data-testid="delete-program-name"
              className="w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--state-danger)]"
            />
          </label>

          <div className="flex flex-wrap items-center justify-end gap-3">
            <Button variant="bare" onPress={() => setConfirming(false)} disabled={!!busy} data-testid="delete-program-cancel">
              Cancel
            </Button>
            <Button
              variant="danger"
              icon="trash"
              onPress={remove}
              disabled={!!busy || !nameMatches}
              data-testid="delete-program-confirm"
            >
              {busy === "delete" ? "Deleting…" : "Delete program"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
